import { ConditionalLink } from "components/conditionalLink";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Slider from "components/DesignSystem/Slider/Slider";
import { FilePreviewTag } from "components/FilePreviewTag/FilePreviewTag";
import { Cross } from "components/icons/Cross";
import { DownloadIcon } from "components/icons/Download";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import GustoIcon from "static/images/GustoIcon.svg";
import { useGetAllPayrollsQuery } from "store/apis/payroll";
import { Payroll } from "types/Models/payroll";

type GustoSliderProps = {
  open: boolean;
  close: () => void;
  groupId: string;
  entityId: string;
};

export default function GustoSlider({
  open,
  close,
  groupId,
  entityId,
}: GustoSliderProps) {
  const { downloadFile, ...downloadState } = useDownloadFile();
  const [pageNumber, setPageNumber] = useState(1);
  const columnHelper = createColumnHelper<Payroll>();
  const { search } = useLocation();

  const { data } = useGetAllPayrollsQuery(
    {
      entityId,
      page_num: pageNumber,
      search_term: null,
    },
    {
      skip: !entityId,
    }
  );

  const columns = [
    columnHelper.accessor("period", {
      header: () => <div className="t-text-left">PERIOD</div>,
      cell: (info) => (
        <span className="t-text-body-sm t-text-text-60">{info.getValue()}</span>
      ),
      size: 40,
    }),

    columnHelper.accessor("source", {
      header: "REPORTS",
      cell: (info) => {
        const payroll = info.row.original;
        return (
          <FilePreviewTag size="small" file={payroll.file} block>
            <Button
              customType="ghost_icon"
              size="small"
              disabled={downloadState.result.isFetching}
              isLoading={downloadState.result.isFetching}
              onClick={(e) => {
                e.stopPropagation();
                downloadFile({
                  file_id: payroll.file.uuid,
                  group_id: groupId,
                });
              }}
            >
              <DownloadIcon color="currentColor" />
            </Button>
          </FilePreviewTag>
        );
      },
      size: 50,
    }),

    columnHelper.accessor("uuid", {
      header: "",
      cell: (info) => {
        const payrollId = info.getValue();
        return (
          <ConditionalLink
            to={`/books/payroll/${payrollId}${search}`}
            target="_blank"
          >
            <Button customType="ghost">View</Button>
          </ConditionalLink>
        );
      },
      size: 10,
    }),
  ];

  const table = useReactTable({
    data: data?.payrolls || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    meta: {
      entityId,
    },
  });

  return (
    <>
      <Slider.Root open={open} onOpenChange={close}>
        <Slider.Content>
          <Slider.Header>
            <Slider.Title className="t-flex t-items-center t-gap-2">
              <img
                src={GustoIcon}
                className="t-rounded-full t-w-10 t-h-10"
                alt=""
              />
              <p className="t-m-0 t-text-subtitle-lg">Gusto</p>
            </Slider.Title>
            <Slider.Close />
          </Slider.Header>
          <Slider.Body>
            <Accordion.Root
              defaultValue={["PAYROLL_FILE"]}
              type="multiple"
              className="t-flex t-flex-col t-gap-4"
            >
              <Accordion.Item value="PAYROLL_FILE">
                <Accordion.Trigger disabled>
                  <div className="t-flex t-justify-between t-items-center t-w-full">
                    Reports imported
                    <PaginationHandler
                      page_num={data?.current_page || 1}
                      per_page={data?.per_page || 10}
                      total_count={data?.total_count || 0}
                      total_pages={data?.total_pages || 1}
                      onPageNumChange={(page) => setPageNumber(page)}
                    />
                  </div>
                </Accordion.Trigger>
                <Accordion.Content>
                  <TableUI table={table} />
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          </Slider.Body>
        </Slider.Content>
      </Slider.Root>
    </>
  );
}
